// PreviewTester/components/LayoutChoicePopup.js
// === STEP 4: CLARIFY CANCEL BEHAVIOR ===
//
// Changes vs your current file:
//   - Cancel button text now says "Cancel — Use Single Screen"
//   - Small italic hint below the cancel button explains the default
//   - Everything else unchanged (Single / Multiple flow, step 2 array picker)

import React, { useState } from 'react';

const LayoutChoicePopup = ({
  isOpen,
  onClose,
  onConfirmSingleScreen,
  onConfirmMultipleScreens,
  detectedArrays,
  parsedResponse,
}) => {
  const [step, setStep] = useState(1);
  const [selectedArrayPath, setSelectedArrayPath] = useState(null);

  if (!isOpen || !detectedArrays) return null;

  const { allArrays = [], nestedArrays = [] } = detectedArrays;

  // Only top-level arrays can be outer loops
  const topLevelArrays = allArrays.filter((a) => !a.isNested);

  // ---------- Step 1 handlers ----------
  const handleSingleScreen = () => {
    console.log('[LayoutChoicePopup] 📄 User chose Single Screen');
    setStep(1);
    setSelectedArrayPath(null);
    onConfirmSingleScreen();
  };

  const handleMultipleScreens = () => {
    console.log('[LayoutChoicePopup] 📚 User chose Multiple Screens');
    setStep(2);
  };

  // ---------- Step 2 handlers ----------
  const handleArraySelect = (arrayPath) => {
    setSelectedArrayPath(arrayPath);
  };

  const handleConfirmMultipleScreens = () => {
    if (!selectedArrayPath) return;
    console.log(
      '[LayoutChoicePopup] 📚 Confirming multiple screens with array:',
      selectedArrayPath
    );
    onConfirmMultipleScreens(selectedArrayPath);
    setStep(1);
    setSelectedArrayPath(null);
  };

  const handleBack = () => {
    setStep(1);
    setSelectedArrayPath(null);
  };

  // Helper: does this top-level array contain a nested array?
  const arrayHasNestedArray = (arrayPath) =>
    nestedArrays.some(
      (na) =>
        na.parentArrayPath === arrayPath ||
        na.parentArrayPath === arrayPath.replace(/\[0\]$/, '')
    );

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.6)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backdropFilter: 'blur(4px)',
        animation: 'fadeIn 0.3s ease',
      }}
    >
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; transform: scale(0.95); }
            to { opacity: 1; transform: scale(1); }
          }
          @keyframes slideUp {
            from { transform: translateY(20px); opacity: 0; }
            to { transform: translateY(0); opacity: 1; }
          }
          .layout-choice-option { transition: all 0.2s ease; }
          .layout-choice-option:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 20px rgba(0,0,0,0.12);
          }
          .layout-array-option { transition: all 0.2s ease; }
          .layout-array-option:hover {
            border-color: #4299e1;
            background-color: #ebf8ff;
          }
        `}
      </style>

      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          padding: '32px 40px',
          maxWidth: '640px',
          width: '90%',
          maxHeight: '85vh',
          overflowY: 'auto',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          animation: 'slideUp 0.3s ease',
          position: 'relative',
        }}
      >
        {/* ============================================================
            STEP 1: Choose layout type
            ============================================================ */}
        {step === 1 && (
          <>
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <div style={{ fontSize: '48px', marginBottom: '8px' }}>📐</div>
              <h2
                style={{
                  fontSize: '22px',
                  fontWeight: '700',
                  color: '#2d3748',
                  margin: '0 0 8px 0',
                }}
              >
                Choose Your Layout
              </h2>
              <p
                style={{
                  fontSize: '14px',
                  color: '#4a5568',
                  lineHeight: '1.6',
                  margin: 0,
                }}
              >
                Your response contains{' '}
  <strong>
    {topLevelArrays.length === 1
      ? 'an array'
      : `${topLevelArrays.length} arrays`}
  </strong>
  . How would you like to display it?
              </p>
            </div>

            {/* Option 1: Single Screen */}
            <button
              onClick={handleSingleScreen}
              className="layout-choice-option"
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                padding: '16px 20px',
                backgroundColor: '#f7fafc',
                border: '2px solid #e2e8f0',
                borderRadius: '10px',
                cursor: 'pointer',
                marginBottom: '12px',
                fontFamily: 'inherit',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  marginBottom: '6px',
                }}
              >
                <span style={{ fontSize: '24px' }}>📄</span>
                <span
                  style={{
                    fontWeight: '700',
                    fontSize: '15px',
                    color: '#2d3748',
                  }}
                >
                  Single Screen
                </span>
              </div>
              <div
                style={{
                  fontSize: '12px',
                  color: '#718096',
                  paddingLeft: '36px',
                  lineHeight: '1.5',
                }}
              >
                All data appears on one page.{' '}
                <strong style={{ color: '#e53e3e' }}>
                  ⚠️ Nested loops not allowed (1 loop max).
                </strong>
              </div>
            </button>

            {/* Option 2: Multiple Screens */}
            <button
              onClick={handleMultipleScreens}
              className="layout-choice-option"
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                padding: '16px 20px',
                backgroundColor: '#f0f9ff',
                border: '2px solid #bee3f8',
                borderRadius: '10px',
                cursor: 'pointer',
                marginBottom: '16px',
                fontFamily: 'inherit',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  marginBottom: '6px',
                }}
              >
                <span style={{ fontSize: '24px' }}>📚</span>
                <span
                  style={{
                    fontWeight: '700',
                    fontSize: '15px',
                    color: '#2b6cb0',
                  }}
                >
                  Multiple Screens
                </span>
              </div>
              <div
                style={{
                  fontSize: '12px',
                  color: '#4a5568',
                  paddingLeft: '36px',
                  lineHeight: '1.5',
                }}
              >
                Each item gets its own screen.{' '}
                <strong style={{ color: '#38a169' }}>
                  ✅ Outer loop creates screens, inner loop shows details.
                </strong>
              </div>
            </button>

            {/* Cancel — clarifies default behavior */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                marginTop: '8px',
                gap: '6px',
              }}
            >
              <button
                onClick={onClose}
                style={{
                  padding: '8px 20px',
                  backgroundColor: 'transparent',
                  color: '#718096',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '500',
                  fontFamily: 'inherit',
                }}
              >
                Cancel — Use Single Screen
              </button>
              <div
                style={{
                  fontSize: '11px',
                  color: '#a0aec0',
                  fontStyle: 'italic',
                  textAlign: 'center',
                  maxWidth: '360px',
                  lineHeight: '1.4',
                }}
              >
                Cancelling will default to <strong>Single Screen</strong>. You
                can change your mind by pasting the response again.
              </div>
            </div>
          </>
        )}

        {/* ============================================================
            STEP 2: Choose which array creates screens
            ============================================================ */}
        {step === 2 && (
          <>
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <div style={{ fontSize: '48px', marginBottom: '8px' }}>📚</div>
              <h2
                style={{
                  fontSize: '22px',
                  fontWeight: '700',
                  color: '#2d3748',
                  margin: '0 0 8px 0',
                }}
              >
                Which Array Creates Screens?
              </h2>
              <p
                style={{
                  fontSize: '14px',
                  color: '#4a5568',
                  lineHeight: '1.6',
                  margin: 0,
                }}
              >
                Select the array that will create multiple screens. Each item in
                this array will be rendered as its own screen.
              </p>
            </div>

            {topLevelArrays.length === 0 && (
              <div
                style={{
                  padding: '16px',
                  backgroundColor: '#fff5f5',
                  borderRadius: '8px',
                  border: '1px solid #fed7d7',
                  fontSize: '13px',
                  color: '#9b2c2c',
                  textAlign: 'center',
                  marginBottom: '16px',
                }}
              >
                ⚠️ No top-level arrays found in your data. Multiple screens
                require a top-level array (like{' '}
                <code>response_data.bookings</code>).
              </div>
            )}

            <div style={{ marginBottom: '16px' }}>
              {topLevelArrays.map((arr) => {
                const isSelected = selectedArrayPath === arr.path;
                const displayName = arr.path.replace(/^response_data\./, '');
                const hasNested = arrayHasNestedArray(arr.path);

                return (
                  <button
                    key={arr.path}
                    onClick={() => handleArraySelect(arr.path)}
                    className="layout-array-option"
                    style={{
                      display: 'block',
                      width: '100%',
                      textAlign: 'left',
                      padding: '14px 16px',
                      backgroundColor: isSelected ? '#ebf8ff' : 'white',
                      border: `2px solid ${
                        isSelected ? '#4299e1' : '#e2e8f0'
                      }`,
                      borderRadius: '10px',
                      cursor: 'pointer',
                      marginBottom: '10px',
                      fontFamily: 'inherit',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: '4px',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                        }}
                      >
                        <span style={{ fontSize: '16px' }}>
                          {isSelected ? '🔵' : '⚪'}
                        </span>
                        <span
                          style={{
                            fontWeight: '700',
                            fontSize: '14px',
                            color: isSelected ? '#2b6cb0' : '#2d3748',
                            fontFamily: 'monospace',
                          }}
                        >
                          {displayName}
                        </span>
                        <span
                          style={{
                            fontSize: '11px',
                            color: '#718096',
                            backgroundColor: '#edf2f7',
                            padding: '2px 8px',
                            borderRadius: '10px',
                          }}
                        >
                          {arr.length} items
                        </span>
                      </div>
                    </div>
                    <div
                      style={{
                        fontSize: '11px',
                        color: '#718096',
                        paddingLeft: '24px',
                        fontFamily: 'monospace',
                      }}
                    >
                      Fields: {arr.fields.join(', ')}
                    </div>
                    {hasNested && (
                      <div
                        style={{
                          fontSize: '10px',
                          color: '#9f7aea',
                          paddingLeft: '24px',
                          marginTop: '4px',
                        }}
                      >
                        📦 Contains nested array(s) — will be used as the inner
                        loop
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            <div
              style={{
                display: 'flex',
                gap: '10px',
                justifyContent: 'space-between',
                marginTop: '16px',
              }}
            >
              <button
                onClick={handleBack}
                style={{
                  padding: '10px 20px',
                  backgroundColor: 'transparent',
                  color: '#4a5568',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '500',
                  fontFamily: 'inherit',
                }}
              >
                ← Back
              </button>
              <button
                onClick={handleConfirmMultipleScreens}
                disabled={!selectedArrayPath}
                style={{
                  padding: '10px 24px',
                  backgroundColor: selectedArrayPath ? '#4299e1' : '#e2e8f0',
                  color: selectedArrayPath ? 'white' : '#a0aec0',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: selectedArrayPath ? 'pointer' : 'not-allowed',
                  fontSize: '13px',
                  fontWeight: '600',
                  fontFamily: 'inherit',
                }}
              >
                Generate Template →
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default LayoutChoicePopup;